import styled from '@emotion/styled';
import type { CustomizationImageAsset, ReferencedAssetPolicy } from '../customization';

export interface LifestyleStripProps extends ReferencedAssetPolicy {
  images?: readonly CustomizationImageAsset[];
  className?: string;
}

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${({ theme }) => theme.space.md};
  width: 100%;
`;

const Figure = styled.figure`
  margin: 0;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.regular};
  background: ${({ theme }) => theme.colors.background.secondary};
  aspect-ratio: 4 / 5;
`;

const Image = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

function canRenderAsset(asset: CustomizationImageAsset, allowReferencedAssets: boolean): boolean {
  if (asset.licenseRisk === 'blocked') return false;
  if (asset.source === 'referenced' && !allowReferencedAssets) return false;
  return asset.src.trim().length > 0;
}

export function LifestyleStrip({
  images = [],
  allowReferencedAssets = false,
  className,
}: LifestyleStripProps) {
  const renderableImages = images.filter((asset) => canRenderAsset(asset, allowReferencedAssets));

  if (renderableImages.length === 0) {
    return null;
  }

  return (
    <Strip className={className}>
      {renderableImages.map((asset) => (
        <Figure key={asset.assetId}>
          <Image
            src={asset.src}
            alt={asset.alt ?? ''}
            width={asset.width}
            height={asset.height}
            loading="lazy"
          />
        </Figure>
      ))}
    </Strip>
  );
}
